import { useEffect, useRef, useState } from 'react'
import type { PlayerColor } from '@/shared/board/types'
import type { FeedEvent } from '@/shared/game/types'

/** Сколько живёт подпись у двора и вспышка на клетке. */
const LIFE_MS = 2200

/** Что говорит подпись — коротко, как реплика: её читают краем глаза. */
const SAY: Record<string, string> = {
  chest: 'сундук!',
  roll: 'ещё бросок!',
  shield: 'щит на 5 ходов!',
}

/**
 * Что нашлось в сундуке — именно это и надо сказать: «сундук!» без
 * содержимого оставлял гадать, что он дал.
 */
function sayChest(prize: unknown, coins: unknown): string {
  if (prize === 'coins') return `сундук: +${Number(coins)} монет!`
  if (prize === 'roll') return 'сундук: ещё бросок!'
  if (prize === 'shield') return 'сундук: щит!'

  return SAY.chest
}

type Callout = { seq: number; color: PlayerColor; text: string; cell: number; until: number }

/**
 * Подбор бонуса — подписью у двора и вспышкой на клетке (docs/BONUSES.md).
 *
 * Без этого бонус просто исчезал с клетки, и что именно случилось —
 * сундук или щит, — надо было угадывать.
 *
 * Показывается в такт очереди показа, а не по приходу события: сервер
 * пишет подбор сразу за ходом, и если показать его сразу, подпись
 * «сундук!» появлялась бы, пока фишка ещё едет. Поэтому событие ждёт, пока
 * курсор очереди дойдёт до хода перед ним (подбор — всегда следующий номер).
 *
 * История не показывается: при входе в партию и после обрыва связи
 * приезжает весь хвост ленты, и доска покрылась бы вспышками разом.
 */
export function useBonusCallouts(
  feed: FeedEvent[] | undefined,
  cursor: number | null,
): { speech: { color: PlayerColor; text: string }[]; flashes: number[] } {
  const [callouts, setCallouts] = useState<Callout[]>([])
  const seen = useRef<number | null>(null)

  useEffect(() => {
    if (feed === undefined || cursor === null) return

    if (seen.current === null) {
      seen.current = cursor

      return
    }

    const before = seen.current
    const fresh = feed.filter(
      (event) =>
        event.type === 'bonus' &&
        event.payload.action === 'take' &&
        event.seq > before &&
        event.seq - 1 <= cursor,
    )

    if (fresh.length === 0) return

    seen.current = Math.max(...fresh.map((event) => event.seq))

    const until = Date.now() + LIFE_MS

    setCallouts((shown) => [
      ...shown,
      ...fresh.map((event) => ({
        seq: event.seq,
        color: event.payload.color as PlayerColor,
        text:
          event.payload.kind === 'chest'
            ? sayChest(event.payload.prize, event.payload.coins)
            : (SAY[String(event.payload.kind)] ?? 'бонус!'),
        cell: Number(event.payload.cell),
        until,
      })),
    ])
  }, [feed, cursor])

  // Гашение — одним таймером на ближайший срок, как у пузырей чата.
  useEffect(() => {
    if (callouts.length === 0) return

    const soonest = Math.min(...callouts.map((callout) => callout.until))
    const timer = setTimeout(
      () => {
        const now = Date.now()

        setCallouts((shown) => shown.filter((callout) => callout.until > now))
      },
      Math.max(0, soonest - Date.now()),
    )

    return () => clearTimeout(timer)
  }, [callouts])

  return {
    speech: callouts.map((callout) => ({
      color: callout.color,
      text: callout.text,
    })),
    flashes: callouts.map((callout) => callout.cell),
  }
}
